import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import apiClient from 'api/client';
import { fetchTemplates } from 'api/actions/templates';
import { toast } from 'components/Shared/Toast';
import { NextBtn, BackBtn } from 'components/Shared/Button';
import { useForm, Controller } from 'react-hook-form';
import useReferenceComponent from 'hooks/useReferenceComponent';
import ReferenceDocument from '../ReferenceDocument';
import { Row, Input, Col, Form, Label, FormFeedback, InputGroup, CustomInput, FormGroup } from 'reactstrap';
import { StepContainer } from './StepContainer';

const QuestionnaireStep = ({ handleBack, onComplete, showReference }) => {
    const { t } = useTranslation();
    const { register, handleSubmit, control, watch, errors } = useForm({
        shouldFocusError: false,
    });
    const [templatesOptions, SetTemplatesOptions] = useState();
    const [reference, handleRefChange] = useReferenceComponent();

    const templateChoosen = watch('template');
    let questions = templateChoosen ? templatesOptions.find((t) => t.id === templateChoosen.value).questions : null;

    const loadTemplates = async (search) => {
        try {
            const { data } = await apiClient(
                fetchTemplates({
                    questionnaireOnly: true,
                    search: search && `name|${search}`,
                })
            );
            SetTemplatesOptions(data.docs);
            return data.docs.map((template) => ({
                label: template.name,
                value: template.id,
                template,
            }));
        } catch (error) {
            console.error(error);
            toast.error(t('Error at fetching templates'));
            return [];
        }
    };
    const handleOnComplete = (data) => {
        if (reference.join && reference.documentMissing) {
            return;
        }
        onComplete(data, reference);
    };

    return (
        <StepContainer
            className="sidebar-new-contract-height"
            title={t('contracts.new.source_questionnaire')}
            src={'/images/listing/createDocument/examen.svg'}
            alt={t('contracts.new.source_template')}
            form={
                <Form onSubmit={handleOnComplete} className="">
                    <Label className="font-weight-bold">{t('contracts.new.template_choose')}</Label>
                    <Controller
                        as={AsyncSelect}
                        name="template"
                        styles={{
                            menu: (provided) => ({ ...provided, zIndex: 3 }),
                        }}
                        control={control}
                        rules={{ required: true }}
                        cacheOptions
                        className="create-select"
                        defaultOptions
                        loadOptions={loadTemplates}
                        placeholder={t('contracts.new.template_choose')}
                        noOptionsMessage={() => {
                            return <div>{t('common.no_templates')}</div>;
                        }}
                    />
                    <FormFeedback invalid={errors.template}>{t('contracts.new.template_required')}</FormFeedback>

                    <Label className="font-weight-bold mt-2">{t('contracts.new.name_title')}</Label>

                    <Input
                        name="name"
                        innerRef={register({ required: true })}
                        invalid={errors.name}
                        placeholder={t('contracts.new.name_title')}
                    />

                    <FormFeedback invalid={errors.name}>{t('contracts.new.name_required')}</FormFeedback>
                    <hr />

                    <Label className="font-weight-bold mt-2">{t('contracts.new.questionnaire')}</Label>

                    {questions
                        ? questions.map((ques, index) => (
                            <div key={ques._id} className="mt-2">
                                <span>{index + 1}. {ques.content}</span>
                                <FormGroup className="my-2">
                                    {ques.answers.map((answer, index) => (
                                        <CustomInput
                                            key={answer._id}
                                            className="my-2 mx-1"
                                            name={ques._id}
                                            id={ques._id + '-' + index}
                                            type="radio"
                                            value={answer._id}
                                            innerRef={register({ required: true })}
                                            label={answer.content}
                                        />
                                    ))}
                                </FormGroup>
                            </div>
                        ))
                        : (
                            <p><small>{t('frontDesk.no_questions')}</small></p>
                        )
                    }
                    <hr></hr>
                    <ReferenceDocument
                        onChange={handleRefChange}
                        reference={reference}
                        addDividerOnTop={false}
                        showReference={showReference}
                    />
                </Form>
            }
            footer={
                <>
                    <BackBtn onClick={handleBack} label={t('form.exit')} />
                    <NextBtn
                        onClick={handleSubmit((val) => {
                            for (let question of questions) {
                                for (let answer of question.answers) {
                                    if (answer._id === val[question._id]) {
                                        answer.selected = true;
                                        break;
                                    }
                                }
                            }
                            handleOnComplete({ ...val, questions });
                        })}
                        label={t('contracts.listing.page_next')}
                    />
                </>
            }
        />
    );
};

export default QuestionnaireStep;
